
import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import { FaRegComment, FaRegHeart } from "react-icons/fa"
import { useToast } from "../components/ToastProvider"
import "../css/sr.css"

const Search = () => {
  const [searchedArticles, setSearchedArticles] = useState([])
  const [searchedTags, setSearchedTags] = useState([])
  const [searchedUser, setSearchedUser] = useState([])
  const navigate = useNavigate()
  const { keyword } = useParams()
  const showToast = useToast()

  // Pagination state for articles
  const [articleCurrentPage, setArticleCurrentPage] = useState(1)
  const [articleTotalPages, setArticleTotalPages] = useState(1)
  const [articlePageInputValue, setArticlePageInputValue] = useState("")

  // Pagination state for users
  const [userCurrentPage, setUserCurrentPage] = useState(1)
  const [userTotalPages, setUserTotalPages] = useState(1)
  const [userPageInputValue, setUserPageInputValue] = useState("")

  // Loading states
  const [isArticleLoading, setIsArticleLoading] = useState(false)
  const [isUserLoading, setIsUserLoading] = useState(false)

  // Active tab state
  const [activeTab, setActiveTab] = useState("keyword")

  useEffect(() => {
    const fetchSearchData = async () => {
      if (!keyword) return

      setIsArticleLoading(true)
      setIsUserLoading(true)

      try {
        const { data } = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/search`, {
          q: keyword,
          page: 1, // Initial page
        })

        console.log(data)
        setSearchedArticles(data.articleResult)
        setSearchedUser(data.userResult)
        setSearchedTags(data.tagResult)

        // Set pagination data
        setArticleTotalPages(data.pagination.article.totalPages)
        setUserTotalPages(data.pagination.user.totalPages)

        // Reset current pages
        setArticleCurrentPage(1)
        setUserCurrentPage(1)
      } catch (error) {
        console.error("Search error:", error)
        showToast("검색 중 오류가 발생했습니다.", { type: "error" })
      } finally {
        setIsArticleLoading(false)
        setIsUserLoading(false)
      }
    }

    fetchSearchData()
  }, [keyword])

  // Function to fetch articles by page
  const fetchArticlesByPage = async (page) => {
    if (page < 1 || page > articleTotalPages) return

    setIsArticleLoading(true)
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/search/by_page`, {
        q: keyword,
        page,
        type: "article",
      })

      setSearchedArticles(data.results)
      setArticleCurrentPage(data.pagination.currentPage)
    } catch (error) {
      console.error("Error fetching articles by page:", error)
      showToast("게시글을 불러오는 중 오류가 발생했습니다.", { type: "error" })
    } finally {
      setIsArticleLoading(false)
    }
  }

  // Function to fetch users by page
  const fetchUsersByPage = async (page) => {
    if (page < 1 || page > userTotalPages) return

    setIsUserLoading(true)
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/search/by_page`, {
        q: keyword,
        page,
        type: "user",
      })

      setSearchedUser(data.results)
      setUserCurrentPage(data.pagination.currentPage)
    } catch (error) {
      console.error("Error fetching users by page:", error)
      showToast("사용자를 불러오는 중 오류가 발생했습니다.", { type: "error" })
    } finally {
      setIsUserLoading(false)
    }
  }

  // Handle article page navigation
  const goToArticlePage = (page) => {
    if (page >= 1 && page <= articleTotalPages) {
      fetchArticlesByPage(page)
    }
  }

  // Handle user page navigation
  const goToUserPage = (page) => {
    if (page >= 1 && page <= userTotalPages) {
      fetchUsersByPage(page)
    }
  }

  // Handle article page input change
  const handleArticlePageInputChange = (e) => {
    setArticlePageInputValue(e.target.value)
  }

  // Handle user page input change
  const handleUserPageInputChange = (e) => {
    setUserPageInputValue(e.target.value)
  }

  // Handle article page input submission
  const handleArticlePageInputSubmit = () => {
    const pageNumber = Number.parseInt(articlePageInputValue)
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= articleTotalPages) {
      goToArticlePage(pageNumber)
      setArticlePageInputValue("")
    } else {
      showToast(`페이지 번호는 1에서 ${articleTotalPages} 사이여야 합니다.`, { type: "warn" })
    }
  }

  // Handle user page input submission
  const handleUserPageInputSubmit = () => {
    const pageNumber = Number.parseInt(userPageInputValue)
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= userTotalPages) {
      goToUserPage(pageNumber)
      setUserPageInputValue("")
    } else {
      showToast(`페이지 번호는 1에서 ${userTotalPages} 사이여야 합니다.`, { type: "warn" })
    }
  }

  // Generate page numbers for article pagination
  const renderArticlePageNumbers = () => {
    const pageNumbers = []
    const maxVisiblePages = 5

    let startPage = Math.max(1, articleCurrentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(articleTotalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <div
          key={i}
          className={`wa-page-number ${articleCurrentPage === i ? "active" : ""}`}
          onClick={() => goToArticlePage(i)}
        >
          {i}
        </div>,
      )
    }

    return pageNumbers
  }

  // Generate page numbers for user pagination
  const renderUserPageNumbers = () => {
    const pageNumbers = []
    const maxVisiblePages = 5

    let startPage = Math.max(1, userCurrentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(userTotalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <div
          key={i}
          className={`wa-page-number ${userCurrentPage === i ? "active" : ""}`}
          onClick={() => goToUserPage(i)}
        >
          {i}
        </div>,
      )
    }

    return pageNumbers
  }

  return (
    <>
      <div className="sr-center">
        <div className="sr-line" style={{ marginTop: 15 }}>
          <div className="wa-option-box" style={{ marginBottom: 10 }}>
            <div
              className={`wa-option ${activeTab === "keyword" ? "selected" : ""}`}
              onClick={() => {
                setActiveTab("keyword")
                window.location.href = `/search/${keyword}`
              }}
            >
              키워드 검색
            </div>
            <div
              className={`wa-option ${activeTab === "tag" ? "selected" : ""}`}
              onClick={() => {
                setActiveTab("tag")
                window.location.href = `/search/tag/${keyword}`
              }}
            >
              태그 검색
            </div>
          </div>

          {/* Article search results */}
          <div className="wa-card sr-card">
            <h3>글 검색 결과 ({keyword})</h3>

            {isArticleLoading ? (
              <div className="loading-indicator">로딩 중...</div>
            ) : searchedArticles.length === 0 ? (
              <div className="no-results">검색 결과가 없습니다.</div>
            ) : (
              <>
                {searchedArticles.map((article) => (
                  <div
                    key={article.id}
                    className="pf-at-box"
                    onClick={() => (window.location.href = "/community/article/" + article.id)}
                  >
                    {article.previewImage && (
                      <div className="cm-card-list-item-thumb wa-thumb">
                        <img src={article.previewImage || "/placeholder.svg"} alt="" />
                      </div>
                    )}
                    <div className="pf-at-main" style={{marginLeft:10}}>
                      <div className="pf-at-title">{article.title}</div>
                      <div className="pf-at-date">{article.date}</div>
                    </div>
                    <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                      <div className="pf-at-ct">
                        <FaRegHeart />
                        {article.like}
                      </div>
                      <div className="pf-at-ct">
                        <FaRegComment />
                        {article.commentCount}
                      </div>
                    </div>
                  </div>
                ))}

                {articleTotalPages > 1 && (
                  <div className="wa-page">
                    {articleCurrentPage > 1 && (
                      <div className="wa-page-number prev" onClick={() => goToArticlePage(articleCurrentPage - 1)}>
                        이전
                      </div>
                    )}

                    {renderArticlePageNumbers()}

                    {articleCurrentPage < articleTotalPages && (
                      <div className="wa-page-number next" onClick={() => goToArticlePage(articleCurrentPage + 1)}>
                        다음
                      </div>
                    )}

                    <input
                      className="wa-page-number input"
                      type="number"
                      placeholder="이동할 페이지 번호"
                      value={articlePageInputValue}
                      onChange={handleArticlePageInputChange}
                      min="1"
                      max={articleTotalPages}
                    />
                    <button className="wa-page-number" onClick={handleArticlePageInputSubmit}>
                      이동
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* User search results */}
          <div className="wa-card sr-card">
            <h3>유저 검색 결과 ({keyword})</h3>

            {isUserLoading ? (
              <div className="loading-indicator">로딩 중...</div>
            ) : searchedUser.length === 0 ? (
              <div className="no-results">검색 결과가 없습니다.</div>
            ) : (
              <>
                {searchedUser.map((user) => (
                  <div
                    key={user.id}
                    className="pf-at-box"
                    onClick={() => (window.location.href = "/profile/" + user.id)}
                  >
                    <div className="sr-profile-img wa-profile">
                      <img src={user.profile || "/placeholder.svg"} alt="프로필 이미지" />
                    </div>
                    <div className="pf-at-main">
                      <div className="pf-at-title">{user.username}</div>
                      <div className="pf-at-date">{user.desc || ""}</div>
                    </div>
                  </div>
                ))}

                {userTotalPages > 1 && (
                  <div className="wa-page">
                    {userCurrentPage > 1 && (
                      <div className="wa-page-number prev" onClick={() => goToUserPage(userCurrentPage - 1)}>
                        이전
                      </div>
                    )}

                    {renderUserPageNumbers()}

                    {userCurrentPage < userTotalPages && (
                      <div className="wa-page-number next" onClick={() => goToUserPage(userCurrentPage + 1)}>
                        다음
                      </div>
                    )}

                    <input
                      className="wa-page-number input"
                      type="number"
                      placeholder="이동할 페이지 번호"
                      value={userPageInputValue}
                      onChange={handleUserPageInputChange}
                      min="1"
                      max={userTotalPages}
                    />
                    <button className="wa-page-number" onClick={handleUserPageInputSubmit}>
                      이동
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

        
        </div>
      </div>
    </>
  )
}

export default Search

